import { isServer } from "./constant";
import { Level, reportError } from "./owl";

/** H5Guard 兜底加载的单例 Promise */
let h5guardPromise: Promise<void> | null = null;

/**
 * H5Guard 兜底加载
 *
 * 正常情况下 H5Guard 由模板（index.ejs / external.ejs）在页面加载时首选加载。
 * 本函数作为兜底：当请求发出时发现 H5guard 仍未加载（模板加载失败或尚未完成），
 * 则动态加载并初始化，确保加签能力可用。
 *
 * - 服务端环境直接 resolve
 * - H5guard 已存在则直接 resolve
 * - 加载失败或超时时降级放行（resolve，不阻塞请求）
 * - 全局单例，多次调用只加载一次
 */
export function loadH5Guard(): Promise<void> {
  if (isServer) {
    return Promise.resolve();
  }

  // 模板已加载成功
  if (typeof H5guard !== "undefined") {
    return Promise.resolve();
  }

  // 单例：避免重复加载
  if (h5guardPromise) {
    return h5guardPromise;
  }

  h5guardPromise = new Promise<void>((resolve) => {
    try {
      const script = document.createElement("script");
      script.setAttribute(
        "src",
        "https://msp.meituan.net/h5guard/H5guard.js",
      );
      script.setAttribute("crossorigin", "anonymous");
      document.body.appendChild(script);

      // 超时兜底：2s 内未加载完则放行
      const timer = setTimeout(() => {
        reportError({
          name: "[loadH5Guard] 兜底加载超时",
          msg: "H5Guard 脚本 2s 内未加载完成，降级放行",
          category: "jsError",
          level: Level.WARN,
        });
        resolve();
      }, 2000);

      script.onload = function () {
        clearTimeout(timer);
        if (window.H5guard) {
          try {
            const searchParams = new URLSearchParams(
              window.location.search || "",
            );
            let openId = searchParams.get("openId") || "";
            if (!openId) {
              const hash = window.location.hash || "";
              const qIndex = hash.indexOf("?");
              if (qIndex !== -1) {
                const hashSearch = new URLSearchParams(hash.slice(qIndex + 1));
                openId = hashSearch.get("openId") || "";
              }
            }
            window._user_open_id = openId;

            H5guard!.init!({
              xhrHook: true,
              fetchHook: true,
              domains: [
                "peppermall.meituan.com",
                "media.meituan.com",
                "media.st.meituan.com",
                "pepper.mall.test.sankuai.com",
                "mall.pepper.test.sankuai.com",
                "click.tsp.test.sankuai.com",
                "click.st.meituan.com",
                "click.meituan.com",
                "click.tsp.st.sankuai.com",
              ],
              openId: openId,
              geo: false,
            });
          } catch (error) {
            reportError({
              name: "[loadH5Guard] 兜底 init 失败",
              msg: error instanceof Error ? error.message : String(error),
              category: "jsError",
              level: Level.WARN,
            });
          }
        }
        resolve();
      };

      script.onerror = function () {
        clearTimeout(timer);
        reportError({
          name: "[loadH5Guard] 兜底脚本加载失败",
          msg: "H5Guard 脚本加载失败，降级放行",
          category: "jsError",
          level: Level.WARN,
        });
        resolve();
      };
    } catch (error) {
      reportError({
        name: "[loadH5Guard] 兜底加载异常",
        msg: error instanceof Error ? error.message : String(error),
        category: "jsError",
        level: Level.WARN,
      });
      resolve();
    }
  });

  return h5guardPromise;
}
